import { Resize } from '@monorepo/shared/components';
import { useEffect, useState } from 'react';
import {
  Col,
  Grid,
  Popover,
  Progress,
  Row,
  Stack,
  Tooltip,
  VStack,
  Whisper
} from 'rsuite';
import {
  formatMbToGb,
  formatPercent,
  useAppSelector,
  getNavigatorLanguage
} from '@monorepo/shared';
import { InstanceAction } from '../../config/const';
import { selectInstances } from '@monorepo/shared/store/instanceSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';
import { selectCurrentLocale } from '@monorepo/shared/store/authSlice';
import { t } from 'i18next';
import { QuotaHistory } from './QuotaHistory';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { FaThList } from 'react-icons/fa';
interface IProps {
  rowData: any;
}

const localeToNavigatorLanguage = (locale: string): string => {
  let str = locale || '';
  if (str === 'auto') {
    str = getNavigatorLanguage();
  }
  const arr = str.match(/.{1,2}/g);
  if (arr?.length) {
    return arr.join('-');
  }
  return locale;
};

const Resource = ({ rowData }: IProps) => {
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const currentGroups = useAppSelector(selectCurrentGroup);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isNoAccess, setIsNoAccess] = useState<boolean>(false);
  const [extraInfo, setExtraInfo] = useState<Record<string, any>>({});
  const [quotaHistoryData, setQuotaHistoryData] = useState<Record<string, any>>(
    {}
  );
  const [quotaHistoryError, setQuotaHistoryError] = useState<
    Record<string, any>
  >({});
  const getColor = (percents: number) => {
    if (percents >= 90) {
      return '#d43527';
    } else if (percents >= 80) {
      return '#e28722';
    } else if (percents >= 70) {
      return '#f3bb2d';
    } else {
      return undefined;
    }
  };
  const currentLocale: string = useAppSelector(selectCurrentLocale);
  const [localeVal, setLocaleVal] = useState<any>('en-US');

  useEffect(() => {
    setLocaleVal(localeToNavigatorLanguage(currentLocale));
  }, [currentLocale]);

  useEffect(() => {
    const data = instances?.[currentGroups.id]?.[rowData?.account_id];
    const extraInfo =
      instancesExtraInfo?.[currentGroups.id]?.[rowData?.account_id];
    if (data) {
      setInstance({ ...data });
      setIsExpired(data?.is_expired);
      setIsNoAccess(data?.service_setting === 'NO_ACCESS');
    }
    if (extraInfo) {
      setIsActive(extraInfo?.is_active);
      setExtraInfo(extraInfo);
    }
    if (data?.quotaHistoryData) {
      setQuotaHistoryData(data?.quotaHistoryData);
    } else if (data?.quotaHistoryError) {
      setQuotaHistoryError(data?.quotaHistoryError);
    }
  }, [instances, instancesExtraInfo]);

  const speaker = (
    <Popover title="" style={{ padding: 0 }}>
      {quotaHistoryData && (
        <QuotaHistory
          quotaHistoryData={quotaHistoryData}
          localeVal={localeVal}
        />
      )}
      {quotaHistoryError && <div>{quotaHistoryError.error_desc}</div>}
    </Popover>
  );
  return (
    <VStack alignItems="stretch" spacing={10}>
      <Stack spacing={8}>
        <b style={{ fontSize: '16px' }}>
          {t('faz.organization.resource.title')}
        </b>
        {!isNoAccess && !isExpired && isActive && (
          <Resize
            rowData={rowData}
            action={InstanceAction.resize}
            extraInfo={extraInfo}
          />
        )}
      </Stack>

      <div className="resource-content">
        <Row className="show-grid">
          <Col span={12}>
            <Stack direction={'column'} alignItems="center" spacing={10}>
              <div style={{ width: '100px' }}>
                <Progress.Circle
                  percent={formatPercent(instance?.detail?.resource?.percent)}
                  strokeColor={getColor(instance?.detail?.resource?.percent)}
                />
              </div>
              <Stack spacing={10} style={{ fontSize: '0.95em' }}>
                <div>
                  <span>{t('faz.organization.resource.entitled')}:</span>
                  <b style={{ fontSize: '1.2em', padding: '0 4px' }}>
                    {formatMbToGb(instance?.detail?.resource?.size)}
                  </b>
                  <span>{`GB/${t('faz.organization.resource.day')}`}</span>
                </div>
                <Stack spacing={8}>
                  <div>
                    <span>{t('faz.organization.resource.used')}:</span>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="quota-used"
                      speaker={
                        <Tooltip>
                          {instance?.is_average_quota_used_last_6_days
                            ? t(
                                'faz.organization.resource.quotaUsedTooltip.avgQuota'
                              )
                            : t(
                                'faz.organization.resource.quotaUsedTooltip.quotaUsedYesterday'
                              )}
                        </Tooltip>
                      }
                    >
                      <b style={{ fontSize: '1.2em', padding: '0 4px' }}>
                        {formatMbToGb(instance?.detail?.resource?.used)}
                      </b>
                    </Whisper>
                    <span>{`GB/${t('faz.organization.resource.day')}`}</span>
                  </div>
                  {quotaHistoryData?.quota_used_history && (
                    <Whisper
                      placement="right"
                      trigger="hover"
                      controlId="quota-usage-history"
                      speaker={speaker}
                      enterable
                    >
                      <span>
                        <FaThList
                          style={{
                            cursor: 'pointer',
                            padding: '3px',
                            border: '1px solid #333',
                            width: '20px',
                            height: '20px'
                          }}
                        ></FaThList>
                      </span>
                    </Whisper>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Col>
          <Col span={12}>
            <Stack direction={'column'} alignItems="center" spacing={10}>
              <div style={{ width: '100px' }}>
                <Progress.Circle
                  percent={formatPercent(instance?.detail?.disk?.percent)}
                  strokeColor={getColor(instance?.detail?.disk?.percent)}
                />
              </div>
              <Stack spacing={10} style={{ fontSize: '0.95em' }}>
                <div>
                  <span>{t('faz.organization.resource.disk')}:</span>
                  <b style={{ fontSize: '1.2em', padding: '0 4px' }}>
                    {formatMbToGb(instance?.detail?.disk?.size)}
                  </b>
                  <span>{'GB'}</span>
                </div>

                <div>
                  <span>{t('faz.organization.resource.used')}:</span>
                  <b style={{ fontSize: '1.2em', padding: '0 4px' }}>
                    {formatMbToGb(instance?.detail?.disk?.used)}
                  </b>
                  <span>{'GB'}</span>
                </div>
              </Stack>
            </Stack>
          </Col>
        </Row>
      </div>
    </VStack>
  );
};

export default Resource;
